import { ChangeDetectionStrategy, Component, HostListener, inject, signal } from '@angular/core';

import { environment } from '../environments/environment';
import { LoadingService } from './core/loading.service';
import { AccessReportComponent } from './features/operations/access-report.component';
import { LockerOperationsComponent } from './features/operations/locker-operations.component';
import { TrackerComponent } from './features/tracker/tracker.component';
import {
  UtilityTool,
  UtilityToolsComponent,
} from './features/utilities/utility-tools.component';
import { AccessBookingComponent } from './features/workflow/access-booking.component';
import { AccessIdComponent } from './features/workflow/access-id.component';
import { AccessKeyComponent } from './features/workflow/access-key.component';
import { DeliveryOptionsComponent } from './features/workflow/delivery-options.component';
import {
  KeyManagementComponent,
  KeyTool,
} from './features/workflow/key-management.component';

type AppView =
  | 'delivery-options'
  | 'direct-booking'
  | 'access-request'
  | 'access-id'
  | 'access-key'
  | 'key-management'
  | 'locker-operations'
  | 'access-event'
  | 'access-deviation'
  | 'utilities'
  | 'tracker';

type MenuName = 'operations' | 'other';
type NavigationSection = MenuName | 'tracker';

interface MenuItem {
  readonly label: string;
  readonly view: AppView;
  readonly utilityTool?: UtilityTool;
  readonly keyTool?: KeyTool;
}

const OPERATIONS_MENU: readonly MenuItem[] = [
  { label: 'Direct Access Booking', view: 'direct-booking' },
  { label: 'Request Lockedspaces', view: 'delivery-options' },
  { label: 'Access Request', view: 'access-request' },
  { label: 'Get Access Id', view: 'access-id' },
  { label: 'Access Key', view: 'access-key' },
  { label: 'List of Saved Access Key', view: 'key-management', keyTool: 'saved' },
  { label: 'Access Event', view: 'access-event' },
  { label: 'Access Deviation', view: 'access-deviation' },
  { label: 'Revoke Access', view: 'utilities', utilityTool: 'revoke' },
  { label: 'Locker Online Operations', view: 'locker-operations' },
  { label: 'Get Capacity', view: 'utilities', utilityTool: 'capacity' },
  { label: 'Change Compartment', view: 'utilities', utilityTool: 'change-compartment' },
  { label: 'Get HardwareIds', view: 'utilities', utilityTool: 'hardware-ids' },
  { label: 'Open All Compartments', view: 'key-management', keyTool: 'request-all' },
  { label: 'Get All Compartment Keys', view: 'key-management', keyTool: 'retrieve-all' },
  { label: 'Inspect Event', view: 'utilities', utilityTool: 'inspect-event' },
  { label: 'Incident Report', view: 'utilities', utilityTool: 'incident-report' },
  { label: 'Get Incident Report', view: 'utilities', utilityTool: 'incident-status' },
];

const OTHER_SERVICES_MENU: readonly MenuItem[] = [
  { label: 'Get Instructions (AT)', view: 'utilities', utilityTool: 'instructions' },
];

@Component({
  selector: 'app-root',
  imports: [
    AccessBookingComponent,
    AccessIdComponent,
    AccessKeyComponent,
    AccessReportComponent,
    DeliveryOptionsComponent,
    KeyManagementComponent,
    LockerOperationsComponent,
    TrackerComponent,
    UtilityToolsComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (loading.active()) {
      <div class="request-progress" role="progressbar" aria-label="Request in progress"></div>
    }
    <header class="app-header">
      <div>
        <p class="eyebrow">PN Sesam</p>
        <h1>{{ title }}</h1>
      </div>
    </header>

    @if (expandedMenu()) {
      <button
        type="button"
        class="menu-backdrop"
        aria-label="Close navigation menu"
        (click)="closeMenus()"
      ></button>
    }

    <nav class="app-navigation" aria-label="Test client tools">
      <div class="app-navigation-inner">
        @if (!standaloneTracker) {
          <div class="nav-menu">
            <button
              type="button"
              class="app-nav-tab"
              [class.active]="activeSection() === 'operations'"
              aria-haspopup="menu"
              aria-controls="operations-menu"
              [attr.aria-expanded]="expandedMenu() === 'operations'"
              (click)="toggleMenu('operations', $event)"
            >
              Operations <span class="menu-caret" aria-hidden="true"></span>
            </button>
            @if (expandedMenu() === 'operations') {
              <div id="operations-menu" class="nav-dropdown" role="menu">
                @for (item of operationsMenu; track item.label) {
                  <button
                    type="button"
                    role="menuitem"
                    [class.selected]="isItemActive(item, 'operations')"
                    [attr.aria-current]="isItemActive(item, 'operations') ? 'page' : null"
                    (click)="openMenuItem(item, 'operations')"
                  >
                    {{ item.label }}
                  </button>
                }
              </div>
            }
          </div>
        }

        <button
          type="button"
          class="app-nav-tab"
          [class.active]="activeSection() === 'tracker'"
          (click)="openTracker()"
        >
          Transaction Tracker
        </button>

        @if (!standaloneTracker) {
          <div class="nav-menu align-end">
            <button
              type="button"
              class="app-nav-tab"
              [class.active]="activeSection() === 'other'"
              aria-haspopup="menu"
              aria-controls="other-services-menu"
              [attr.aria-expanded]="expandedMenu() === 'other'"
              (click)="toggleMenu('other', $event)"
            >
              Other Services <span class="menu-caret" aria-hidden="true"></span>
            </button>
            @if (expandedMenu() === 'other') {
              <div id="other-services-menu" class="nav-dropdown" role="menu">
                @for (item of otherServicesMenu; track item.label) {
                  <button
                    type="button"
                    role="menuitem"
                    [class.selected]="isItemActive(item, 'other')"
                    [attr.aria-current]="isItemActive(item, 'other') ? 'page' : null"
                    (click)="openMenuItem(item, 'other')"
                  >
                    {{ item.label }}
                  </button>
                }
              </div>
            }
          </div>
        }
      </div>
    </nav>

    <main class="content page-content">
      @switch (activeView()) {
        @case ('delivery-options') {
          <app-delivery-options />
        }
        @case ('direct-booking') {
          <app-access-booking [directBooking]="true" />
        }
        @case ('access-request') {
          <app-access-booking />
        }
        @case ('access-id') {
          <app-access-id />
        }
        @case ('access-key') {
          <app-access-key />
        }
        @case ('key-management') {
          <app-key-management [initialTool]="keyTool()" [showNavigation]="false" />
        }
        @case ('locker-operations') {
          <app-locker-operations />
        }
        @case ('access-event') {
          <app-access-report mode="event" />
        }
        @case ('access-deviation') {
          <app-access-report mode="deviation" />
        }
        @case ('utilities') {
          <app-utility-tools [initialTool]="utilityTool()" [showNavigation]="false" />
        }
        @case ('tracker') {
          <app-tracker />
        }
      }
    </main>
  `,
})
export class App {
  protected readonly loading = inject(LoadingService);
  protected readonly standaloneTracker = environment.standaloneTracker;
  protected readonly operationsMenu = OPERATIONS_MENU;
  protected readonly otherServicesMenu = OTHER_SERVICES_MENU;
  protected readonly title = environment.standaloneTracker
    ? 'Transaction Tracker'
    : 'Adaptor Test Client';
  protected readonly activeView = signal<AppView>(
    environment.standaloneTracker ? 'tracker' : 'direct-booking',
  );
  protected readonly activeSection = signal<NavigationSection>(
    environment.standaloneTracker ? 'tracker' : 'operations',
  );
  protected readonly expandedMenu = signal<MenuName | null>(null);
  protected readonly utilityTool = signal<UtilityTool>('revoke');
  protected readonly keyTool = signal<KeyTool>('saved');

  protected toggleMenu(menu: MenuName, event: MouseEvent): void {
    event.stopPropagation();
    this.expandedMenu.update((current) => (current === menu ? null : menu));
  }

  protected openMenuItem(item: MenuItem, section: MenuName): void {
    if (item.utilityTool) {
      this.utilityTool.set(item.utilityTool);
    }
    if (item.keyTool) {
      this.keyTool.set(item.keyTool);
    }
    this.activeView.set(item.view);
    this.activeSection.set(section);
    this.expandedMenu.set(null);
  }

  protected openTracker(): void {
    this.activeView.set('tracker');
    this.activeSection.set('tracker');
    this.expandedMenu.set(null);
  }

  protected isItemActive(item: MenuItem, section: MenuName): boolean {
    return (
      this.activeSection() === section &&
      this.activeView() === item.view &&
      (!item.utilityTool || this.utilityTool() === item.utilityTool) &&
      (!item.keyTool || this.keyTool() === item.keyTool)
    );
  }

  @HostListener('document:click')
  @HostListener('document:keydown.escape')
  protected closeMenus(): void {
    this.expandedMenu.set(null);
  }
}